/**
 * @file StockBreakdownSection.tsx
 * @description Секция «Остатки» в карточке товара (phase19.14, экран D шага 0).
 *
 * Показывает фасовка × склад: физический / резерв / доступно, с раскрытием
 * партий (срок годности и адрес ячейки). Непродажные склады (карантин,
 * транзит) видны, но помечены — они не входят в «доступно» (W9).
 *
 * Ошибка склада не должна ронять карточку товара: при сбое рисуем
 * «Остатки временно недоступны», остальная карточка работает.
 */

import { Boxes, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { StockWarehouseRow } from "./StockWarehouseRow";
import { SectionCard } from "./SectionCard";
import { Alert } from "../../../../components/ui/alert";
import { ApiError } from "../../../../lib/api-client";
import {
  formatStockSkuLabel,
  productStockApi,
  type ProductStockDto,
} from "../../../../lib/api-product-stock";

interface Props {
  productId: string;
  /** Перечитать после перестановки партии в другую ячейку. */
  refreshToken?: number;
}

export function StockBreakdownSection({ productId, refreshToken = 0 }: Props) {
  const [stock, setStock] = useState<ProductStockDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    productStockApi
      .get(productId)
      .then((data) => {
        if (cancelled) return;
        setStock(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : "Остатки временно недоступны",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId, refreshToken, reloadKey]);

  const title = stock?.available
    ? `Остатки · доступно ${stock.available}`
    : "Остатки";

  return (
    <SectionCard icon={<Boxes />} title={title} collapsible defaultExpanded>
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-primary" />
        </div>
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : !stock || stock.stockTracked === false ? (
        <p className="text-sm text-on-surface-variant">
          Складской учёт для этого типа товара не ведётся.
        </p>
      ) : stock.skus.length === 0 ? (
        <p className="text-sm text-on-surface-variant">
          Движений по складу ещё не было. Оприходуйте товар через приёмку.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-on-surface-variant">
            «Доступно» считается по складам, доступным для продажи.
          </p>
          {stock.skus.map((sku) => (
            <div key={sku.skuId} className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium">
                  {formatStockSkuLabel(sku)}
                </span>
                <span className="text-sm tabular-nums text-on-surface-variant">
                  доступно {sku.available}
                </span>
              </div>
              {sku.warehouses.length === 0 ? (
                <p className="text-xs text-on-surface-variant">
                  По этой фасовке движений не было.
                </p>
              ) : (
                sku.warehouses.map((row) => (
                  <StockWarehouseRow
                    key={`${sku.skuId}-${row.warehouse.id}`}
                    row={row}
                    onLotMoved={() => setReloadKey((n) => n + 1)}
                  />
                ))
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
